import { IScopeClause } from "@/api/scopes";
import { GuidanceExpandableWidget } from "@/components/shared/ExpandableGuidance";
import { Pill } from "@/components/shared/Pill";
import { styles } from "@/config/project";
import { resolveClauseGuidanceWithSource } from "@/utils/resolveClauseGuidance";
import {
    Comment as CommentIcon,
    Description as PageIcon,
} from "@mui/icons-material";
import { Box, Popover, Typography, styled, useTheme } from "@mui/material";
import React from "react";

const ColumnsContainer = styled(Box)(({ theme }) => ({
    display: "flex",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: "white",
    overflowX: "auto",
}));

const ScopeColumn = styled(Box)(() => ({
    flex: "1 0 300px", // Grow equally, don't shrink below 300px
    minWidth: "300px",
}));

const ScopeHeader = styled(Box)(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1),
}));

const ExcerptCard = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.grey[50],
    borderRadius: "8px",
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    fontSize: "0.75rem",
    lineHeight: 1.5,
    color: theme.palette.text.primary,
    display: "flex",
    flexDirection: "column",
}));

const ExcerptFooter = styled(Box)({
    display: "flex",
    justifyContent: "flex-end",
    marginTop: "8px",
});

interface ExcerptColumnsProps {
    scopes: Array<{
        id: number;
        document_title: string;
        organization_id?: number;
        scope_clauses: IScopeClause[];
    }>;
    clauseId: number;
    clauseName?: string;
}

export const ExcerptColumns: React.FC<ExcerptColumnsProps> = ({
    scopes,
    clauseId,
    clauseName,
}) => {
    const theme = useTheme();
    const [commentAnchor, setCommentAnchor] = React.useState<{
        [key: number]: HTMLElement | null;
    }>({});
    const [openPopoverId, setOpenPopoverId] = React.useState<number | null>(
        null
    );

    const handlePageClick = (scopeId: number, pageIndex: number) => {
        // Navigate to the scope page with page number in a new tab
        window.open(
            `/organizations/scopes/${scopeId}?page=${pageIndex + 1}&clause=${clauseId}`,
            "_blank"
        );
    };

    const handleCommentClick = (
        event: React.MouseEvent<HTMLElement>,
        scopeId: number
    ) => {
        setCommentAnchor({ ...commentAnchor, [scopeId]: event.currentTarget });
        setOpenPopoverId(scopeId);
    };

    const handleCommentClose = (scopeId: number) => {
        setCommentAnchor({ ...commentAnchor, [scopeId]: null });
        setOpenPopoverId(null);
    };

    // Utility function to format guidance attribution
    const formatGuidanceAttribution = (
        userName?: string,
        firstName?: string,
        lastName?: string,
        date?: string
    ) => {
        const displayName =
            firstName && lastName ? `${firstName} ${lastName}` : userName || "";

        if (date) {
            const formattedDate = new Date(date).toLocaleDateString("en-US", {
                month: "numeric",
                day: "numeric",
                year: "2-digit",
            });
            return `Guidance updated by ${displayName} on ${formattedDate}`;
        }

        return `Guidance updated by ${displayName}`;
    };

    return (
        <ColumnsContainer>
            {scopes.map((scope) => {
                // Get all excerpts for this clause in this scope
                const clauseExcerpts = scope.scope_clauses.filter(
                    (sc) => sc.clause.id === clauseId
                );

                // Filter out excerpts that have no content or are not found
                const validExcerpts = clauseExcerpts.filter(
                    (excerpt) =>
                        excerpt.content !== null && excerpt.found === true
                );

                // Log any scope-specific guidance or comments
                clauseExcerpts.forEach((excerpt) => {
                    if (excerpt.guidance) {
                        console.log(
                            `Scope-specific guidance for clause ${clauseName} in ${scope.document_title}:`,
                            excerpt.guidance
                        );
                    }
                    if (excerpt.comments && excerpt.comments.length > 0) {
                        console.log(
                            `Comments for clause ${clauseName} in ${scope.document_title}:`,
                            excerpt.comments
                        );
                    }
                });

                // Sort by page number
                const sortedExcerpts = [...validExcerpts].sort((a, b) => {
                    const pageA =
                        a.highlight_areas.length > 0
                            ? Math.min(
                                  ...a.highlight_areas.map((h) => h.page_index)
                              )
                            : 0;
                    const pageB =
                        b.highlight_areas.length > 0
                            ? Math.min(
                                  ...b.highlight_areas.map((h) => h.page_index)
                              )
                            : 0;
                    return pageA - pageB;
                });

                // Count total comments across all excerpts (including those not found)
                const totalComments = clauseExcerpts.reduce(
                    (sum, excerpt) => sum + (excerpt.comments?.length || 0),
                    0
                );

                const scopeGuidance = clauseExcerpts.find(
                    (excerpt) =>
                        resolveClauseGuidanceWithSource(excerpt).source ===
                        "scope"
                );

                return (
                    <ScopeColumn key={scope.id}>
                        <ScopeHeader>
                            <Typography
                                variant="h6"
                                sx={{ fontWeight: 600, fontSize: "0.9rem" }}
                            >
                                {scope.document_title}
                            </Typography>
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 1,
                                    alignItems: "center",
                                }}
                            >
                                {totalComments > 0 && (
                                    <Pill
                                        icon={<CommentIcon />}
                                        label={totalComments.toString()}
                                        onClick={(e) =>
                                            handleCommentClick(e, scope.id)
                                        }
                                        sx={{
                                            backgroundColor:
                                                theme.palette.tertiary.main,
                                            color: theme.palette.primary.main,
                                            border: `1px solid ${theme.palette.primary.main}`,
                                            cursor: "pointer",
                                            transition: "all 0.2s ease",
                                            "&:hover": {
                                                backgroundColor:
                                                    theme.palette.primary.main,
                                                color: theme.palette.primary
                                                    .contrastText,
                                                transform: "scale(1.05)",
                                            },
                                        }}
                                    />
                                )}
                            </Box>
                        </ScopeHeader>

                        {scopeGuidance && (
                            <Box sx={{ mb: 1 }}>
                                <GuidanceExpandableWidget
                                    title="Additional Guidance"
                                    guidance={scopeGuidance.guidance || ""}
                                    attribution={formatGuidanceAttribution(
                                        scopeGuidance.guidance_created_by
                                            ?.email,
                                        scopeGuidance.guidance_created_by
                                            ?.first_name,
                                        scopeGuidance.guidance_created_by
                                            ?.last_name,
                                        scopeGuidance.created_at
                                    )}
                                    noWrapper={true}
                                />
                            </Box>
                        )}

                        {sortedExcerpts.length > 0 ? (
                            sortedExcerpts.map((excerpt, index) => {
                                const pageNum =
                                    excerpt.highlight_areas.length > 0
                                        ? Math.min(
                                              ...excerpt.highlight_areas.map(
                                                  (h) => h.page_index
                                              )
                                          ) + 1
                                        : null;

                                return (
                                    <ExcerptCard key={index}>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontSize: "0.75rem" }}
                                        >
                                            {excerpt.content}
                                        </Typography>
                                        {pageNum && (
                                            <ExcerptFooter>
                                                <Pill
                                                    icon={<PageIcon />}
                                                    label={`Page ${pageNum}`}
                                                    onClick={() =>
                                                        handlePageClick(
                                                            scope.id,
                                                            pageNum - 1
                                                        )
                                                    }
                                                    sx={{
                                                        backgroundColor:
                                                            theme.palette
                                                                .tertiary.main,
                                                        color: theme.palette
                                                            .primary.main,
                                                        cursor: "pointer",
                                                        "&:hover": {
                                                            backgroundColor:
                                                                theme.palette
                                                                    .primary
                                                                    .main,
                                                            color: theme.palette
                                                                .primary
                                                                .contrastText,
                                                        },
                                                    }}
                                                />
                                            </ExcerptFooter>
                                        )}
                                    </ExcerptCard>
                                );
                            })
                        ) : (
                            <ExcerptCard
                                sx={{
                                    backgroundColor: `${styles.errorLight} !important`,
                                    border: `1px solid ${styles.errorBorder} !important`,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    textAlign: "center",
                                    minHeight: "60px",
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontSize: "0.75rem",
                                        color: styles.errorColor,
                                        fontWeight: 500,
                                    }}
                                >
                                    ⚠️ No equivalent clause found in{" "}
                                    {scope.document_title}
                                </Typography>
                            </ExcerptCard>
                        )}
                    </ScopeColumn>
                );
            })}

            {/* Comment Popovers */}
            {scopes.map((scope) => {
                const clauseExcerpts = scope.scope_clauses.filter(
                    (sc) => sc.clause.id === clauseId
                );
                const allComments = clauseExcerpts.flatMap(
                    (excerpt) => excerpt.comments || []
                );

                return (
                    <Popover
                        key={scope.id}
                        open={openPopoverId === scope.id}
                        anchorEl={commentAnchor[scope.id]}
                        onClose={() => handleCommentClose(scope.id)}
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "right",
                        }}
                        transformOrigin={{
                            vertical: "top",
                            horizontal: "right",
                        }}
                        sx={{
                            "& .MuiPaper-root": {
                                marginTop: 1,
                                padding: 0,
                            },
                            "& .MuiPopover-paper": {
                                padding: 0,
                            },
                        }}
                    >
                        <Box
                            sx={{
                                minWidth: 280,
                                maxWidth: 280,
                                borderRadius: "6px",
                                boxShadow:
                                    "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                                overflow: "hidden",
                                backgroundColor: "white",
                                border: `1px solid ${theme.palette.divider}`,
                                m: 0,
                                p: 0,
                            }}
                        >
                            <Box
                                sx={{
                                    p: 1.5,
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                <div
                                    style={{
                                        fontWeight: 600,
                                        fontSize: "0.75rem",
                                        color: theme.palette.text.primary,
                                        margin: 0,
                                        padding: 0,
                                    }}
                                >
                                    Comments for {scope.document_title}
                                </div>
                                <Typography
                                    component="a"
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();

                                        window.open(
                                            `/organizations/scopes/${scope.id}`,
                                            "_blank"
                                        );
                                    }}
                                    sx={{
                                        fontSize: "0.75rem",
                                        fontWeight: 600,
                                        color: theme.palette.primary.main,
                                        textDecoration: "none",
                                        cursor: "pointer",
                                        display: "block",
                                        mt: 0.5,
                                        "&:hover": {
                                            textDecoration: "underline",
                                        },
                                    }}
                                >
                                    View Scope
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    maxHeight: 200,
                                    overflow: "auto",
                                    p: 1.5,
                                }}
                            >
                                {allComments.length > 0 ? (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 1,
                                        }}
                                    >
                                        {allComments.map((comment, index) => (
                                            <Box
                                                key={index}
                                                sx={{
                                                    pb:
                                                        index <
                                                        allComments.length - 1
                                                            ? 1
                                                            : 0,
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontSize: "0.7rem",
                                                        fontWeight: 600,
                                                        color: theme.palette
                                                            .text.primary,
                                                        mb: 0.25,
                                                    }}
                                                >
                                                    {comment.user?.first_name &&
                                                    comment.user?.last_name
                                                        ? `${comment.user.first_name} ${comment.user.last_name}`
                                                        : comment.user?.email ||
                                                          "Unknown"}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontSize: "0.75rem",
                                                        color: theme.palette
                                                            .text.primary,
                                                        mb: 0.25,
                                                    }}
                                                >
                                                    {comment.comment}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontSize: "0.65rem",
                                                        fontWeight: 600,
                                                        color: theme.palette
                                                            .text.secondary,
                                                        display: "block",
                                                    }}
                                                >
                                                    {new Date(
                                                        comment.created_at
                                                    ).toLocaleDateString(
                                                        "en-US",
                                                        {
                                                            month: "long",
                                                            day: "numeric",
                                                            year: "numeric",
                                                        }
                                                    )}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: theme.palette.text.secondary,
                                            textAlign: "center",
                                            fontStyle: "italic",
                                        }}
                                    >
                                        No comments yet
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Popover>
                );
            })}
        </ColumnsContainer>
    );
};
